/**
 * Attachment Preview Component
 *
 * Full-size modal preview for bug report attachments.
 * Displays image at full resolution with close on click-outside, Escape key, and close button.
 *
 * @module AttachmentPreview
 */

import React, { useCallback, useEffect } from 'react';
import { Portal } from './utils/portal';
import type { ClientAttachment } from '@wds/bug-reporter-types';
import { isImageFile, getFileExtension } from './image-utils';

/**
 * Props for AttachmentPreview component
 */
export interface AttachmentPreviewProps {
  /** Attachment to preview (null = modal closed) */
  attachment: ClientAttachment | null;

  /** Callback when modal is closed */
  onClose?: () => void;

  /** Class name for custom styling */
  className?: string;
}

/**
 * Attachment preview modal component
 *
 * Features:
 * - Full-size image display with max-width/height constraints
 * - Close on Escape key press
 * - Close on click outside image
 * - Close button in top-right corner
 * - Attachment metadata display (type, size, name)
 * - Keyboard accessibility (Escape key, focus management)
 * - Mobile-responsive design
 * - Renders via Portal (outside Shadow DOM)
 * - ARIA attributes for accessibility
 *
 * @example
 * ```tsx
 * <AttachmentPreview
 *   attachment={selectedAttachment}
 *   onClose={() => setSelectedAttachment(null)}
 * />
 * ```
 */
export const AttachmentPreview: React.FC<AttachmentPreviewProps> = ({
  attachment,
  onClose,
  className = '',
}) => {
  /**
   * Handle Escape key press
   * Closes modal if callback provided
   */
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && attachment && onClose) {
        console.info('[AttachmentPreview] Closing modal via Escape key');
        onClose();
      }
    },
    [attachment, onClose]
  );

  /**
   * Handle overlay click
   * Closes modal if clicking outside the image
   */
  const handleOverlayClick = useCallback(
    (event: React.MouseEvent) => {
      // Only close if clicking the overlay (not the image or metadata)
      if (event.target === event.currentTarget && onClose) {
        console.info('[AttachmentPreview] Closing modal via overlay click');
        onClose();
      }
    },
    [onClose]
  );

  /**
   * Handle close button click
   */
  const handleCloseClick = useCallback(() => {
    if (onClose) {
      console.info('[AttachmentPreview] Closing modal via close button');
      onClose();
    }
  }, [onClose]);

  // Register Escape key listener
  useEffect(() => {
    if (attachment) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [attachment, handleKeyDown]);

  // Don't render if no attachment
  if (!attachment) {
    return null;
  }

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <Portal>
      <div
        className={`bug-reporter-attachment-preview ${className}`.trim()}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          zIndex: 2147483647,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          cursor: 'pointer',
        }}
        onClick={handleOverlayClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby="attachment-preview-title"
        data-bug-reporter-attachment-preview
      >
        {/* Content container - stops propagation to prevent closing when clicking content */}
        <div
          style={{
            position: 'relative',
            maxWidth: '90vw',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            cursor: 'default',
          }}
          onClick={(e) => e.stopPropagation()}
          data-bug-reporter-preview-content
        >
          {/* Close Button */}
          <button
            type="button"
            className="bug-reporter-preview-close-btn"
            onClick={handleCloseClick}
            aria-label="Close preview"
            style={{
              position: 'absolute',
              top: '-40px',
              right: 0,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffffff',
              transition: 'opacity 0.15s ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.8')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
            data-bug-reporter-preview-close
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ width: 32, height: 32 }}
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          {/* Image/File Container */}
          <div
            style={{
              backgroundColor: '#1f2937',
              borderRadius: '8px',
              padding: '8px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.3)',
            }}
            data-bug-reporter-preview-image-container
          >
            {attachment.file && isImageFile(attachment.file) ? (
              <img
                id="attachment-preview-title"
                src={attachment.previewUrl}
                alt={attachment.originalName || attachment.type}
                style={{
                  display: 'block',
                  maxWidth: '100%',
                  maxHeight: 'calc(90vh - 120px)', // Leave room for metadata
                  width: 'auto',
                  height: 'auto',
                  borderRadius: '4px',
                  objectFit: 'contain',
                }}
                data-bug-reporter-preview-image
              />
            ) : (
              /* Non-image file placeholder */
              <div
                id="attachment-preview-title"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '60px 80px',
                  backgroundColor: '#374151',
                  borderRadius: '4px',
                  color: '#f9fafb',
                }}
                data-bug-reporter-preview-file
              >
                {/* Large file icon */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ width: 80, height: 80, marginBottom: 16, color: '#9ca3af' }}
                  aria-hidden="true"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                {/* Extension */}
                <span
                  style={{
                    fontSize: 32,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    color: '#d1d5db',
                  }}
                >
                  {getFileExtension(attachment.originalName || '')}
                </span>
                {/* Filename */}
                {attachment.originalName && (
                  <span
                    style={{
                      marginTop: 12,
                      fontSize: 14,
                      color: '#9ca3af',
                      maxWidth: '300px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {attachment.originalName}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Metadata Bar */}
          <div
            style={{
              marginTop: '12px',
              padding: '12px',
              backgroundColor: 'rgba(31, 41, 55, 0.9)',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: '#f9fafb',
              fontSize: '14px',
              flexWrap: 'wrap',
            }}
            data-bug-reporter-preview-metadata
          >
            {/* Type Badge */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 10px',
                borderRadius: '4px',
                backgroundColor: attachment.type === 'screenshot' ? '#3b82f6' : '#10b981',
                fontSize: '12px',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
              data-bug-reporter-preview-type
            >
              {attachment.type === 'screenshot' ? (
                <>
                  {/* Camera Icon */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ width: 14, height: 14 }}
                    aria-hidden="true"
                  >
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                  Screenshot
                </>
              ) : (
                <>
                  {/* Upload Icon */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ width: 14, height: 14 }}
                    aria-hidden="true"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  Upload
                </>
              )}
            </div>

            {/* Separator */}
            <div
              style={{
                width: '1px',
                height: '16px',
                backgroundColor: '#6b7280',
              }}
              aria-hidden="true"
            />

            {/* File Name (truncated) */}
            {attachment.originalName && (
              <>
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '200px',
                  }}
                  data-bug-reporter-preview-filename
                >
                  {attachment.originalName}
                </span>
                <div
                  style={{
                    width: '1px',
                    height: '16px',
                    backgroundColor: '#6b7280',
                  }}
                  aria-hidden="true"
                />
              </>
            )}

            {/* File Size */}
            <span
              style={{
                color: '#9ca3af',
                fontSize: '13px',
              }}
              data-bug-reporter-preview-size
            >
              {formatFileSize(attachment.size)}
            </span>
          </div>
        </div>
      </div>
    </Portal>
  );
};
